/**
 * TourKarte — Leaflet-Karte mit den zugewiesenen Kunden EINER Tour, in
 * Reihenfolge verbunden als Polyline. Kunden ohne Koordinaten werden
 * stillschweigend ausgelassen (zeigen wir als kleinen Stat-Hinweis an).
 *
 * Properties:
 *   tourKunden    — Array aus tour.kunden mit kunde_lat / kunde_lng / kunde_name / status / uhrzeit
 *   onAufsHandy   — Callback () => void, baut Deep-Link und uebergibt ihn
 *
 * Pin-Farben:
 *   - emerald  = bestaetigt
 *   - amber    = in Planung
 *
 * Pin-Inhalt: laufende Nummer (1, 2, 3 …) entsprechend der Sortierung,
 * die das Backend liefert (Bestaetigt nach Uhrzeit, dann Planung).
 */

import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { useEffect } from 'react';

// Icons-URL-Workaround wie in KundenKarte (Vite-Pfad-Problem mit
// Leaflets Default-Markern).
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

// Kleiner brombeer-Tropfen-Pin fuer Kunden, die NICHT auf der Tour sind —
// damit man sieht, wer in der Naehe wohnt und evtl. mit aufgenommen werden
// koennte. Optisch klar getrennt von den nummerierten Tour-Stopps.
function anderePinIcon() {
  const size = 22;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: #9D174D;
      border: 2px solid white;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size - 2],
    popupAnchor: [0, -(size - 2)],
  });
}

// Anker-Pin fuer Start/Ziel (eckig, dunkel) — klar getrennt von den runden
// nummerierten Kunden-Stopps.
function ankerIcon(letter) {
  const html = `
    <div style="
      width: 28px; height: 28px;
      background: #1f2937;
      border: 2px solid white;
      border-radius: 6px;
      box-shadow: 0 2px 5px rgba(0,0,0,0.4);
      display: flex; align-items: center; justify-content: center;
      color: white; font-weight: 700; font-size: 11px;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    ">${letter}</div>`;
  return L.divIcon({ html, className: '', iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14] });
}

function nummerIcon(nummer, status) {
  const farbe = status === 'bestaetigt' ? '#059669' : '#D97706';
  const html = `
    <div style="
      width: 30px; height: 30px;
      background: ${farbe};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
      color: white;
      font-weight: 700;
      font-size: 13px;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    ">${nummer}</div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
}

function FitBoundsAufRoute({ punkte }) {
  const map = useMap();
  useEffect(() => {
    if (!punkte || punkte.length === 0) return;
    if (punkte.length === 1) {
      map.setView([punkte[0].lat, punkte[0].lng], 12);
      return;
    }
    const bounds = L.latLngBounds(punkte.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
  }, [punkte, map]);
  return null;
}

export default function TourRoutenKarte({ tourKunden, alleKunden = [], onAddKunde, startPunkt = null, zielPunkt = null }) {
  const anker = (p) => (p && typeof p.lat === 'number' && typeof p.lng === 'number') ? p : null;
  const aStart = anker(startPunkt);
  const aZiel = anker(zielPunkt);
  const gleicherAnker = aStart && aZiel && Math.abs(aStart.lat - aZiel.lat) < 1e-6 && Math.abs(aStart.lng - aZiel.lng) < 1e-6;
  const punkte = useMemo(() => {
    return (tourKunden || [])
      .map((tk, idx) => ({
        ...tk,
        nummer: idx + 1,
        lat: tk.kunde_lat,
        lng: tk.kunde_lng,
      }))
      .filter(p => typeof p.lat === 'number' && typeof p.lng === 'number');
  }, [tourKunden]);

  const ohneKoords = (tourKunden?.length || 0) - punkte.length;

  // Alle anderen Kunden mit Koordinaten, die NICHT auf dieser Tour sind —
  // als Namensschild-Pins, damit man Nachbarn sieht und ggf. aufnehmen kann.
  const andere = useMemo(() => {
    const tourIds = new Set((tourKunden || []).map(tk => tk.kunde_id));
    return (alleKunden || []).filter(
      k => typeof k.lat === 'number' && typeof k.lng === 'number' && !tourIds.has(k.id)
    );
  }, [alleKunden, tourKunden]);

  if (!tourKunden || tourKunden.length === 0) {
    return null;
  }

  if (punkte.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
        <div className="text-sm text-gray-500 italic">
          Karte nicht verfuegbar — keiner der zugewiesenen Kunden hat Koordinaten.
        </div>
      </div>
    );
  }

  // Polyline-Pfad: (Start?) -> Kunden in Sortierung -> (Ziel?).
  const pfad = [
    ...(aStart ? [[aStart.lat, aStart.lng]] : []),
    ...punkte.map(p => [p.lat, p.lng]),
    ...(aZiel ? [[aZiel.lat, aZiel.lng]] : []),
  ];
  // Anker in die Kartenausschnitt-Berechnung einbeziehen.
  const fitPunkte = [
    ...(aStart ? [aStart] : []),
    ...punkte,
    ...(aZiel ? [aZiel] : []),
  ];

  const center = [punkte[0].lat, punkte[0].lng];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-5">
      <div className="px-5 pt-4 pb-2">
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#9D174D' }}>
          Karte: Tour-Reihenfolge
        </h3>
        <div className="text-xs text-gray-500 mt-0.5">
          {punkte.length} Stopps · gruen = bestaetigt, orange = Planung · brombeer = anderer Kunde in der Naehe
          {ohneKoords > 0 && (
            <span className="text-gray-400"> · {ohneKoords} ohne Koordinaten ausgeblendet</span>
          )}
        </div>
      </div>

      <div style={{ height: '420px', width: '100%' }}>
        <MapContainer
          center={center}
          zoom={11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBoundsAufRoute punkte={fitPunkte} />

          {/* Gestrichelte Linie zwischen den Stopps in Sortier-Reihenfolge.
              Bewusst keine echte Routing-Strecke — wir wuerden sonst eine
              externe Routing-API brauchen (kostet, DSGVO). Die Linie ist
              nur eine Visualisierung der Reihenfolge; gefahren wird ueber
              den Handy-Deep-Link mit echtem Routing in Apple/Google Maps. */}
          <Polyline
            positions={pfad}
            pathOptions={{ color: '#9D174D', weight: 3, dashArray: '6, 8', opacity: 0.7 }}
          />

          {/* Andere Kunden (nicht auf dieser Tour) — geclustert, mit
              Namensschild. Beim Reinzoomen erscheinen die Nachnamen, dichte
              Bereiche fallen zu Zahlen-Clustern zusammen (wie im Umkreis-
              Modal). Klick oeffnet Akte oder nimmt den Kunden in die Tour. */}
          <MarkerClusterGroup chunkedLoading maxClusterRadius={45}>
            {andere.map(k => {
              const nachname = k.lastName || k.firstName || '';
              const vollname = [k.lastName, k.firstName].filter(Boolean).join(', ') || 'Unbenannt';
              return (
                <Marker key={k.id} position={[k.lat, k.lng]} icon={anderePinIcon()}>
                  {nachname && (
                    <Tooltip permanent direction="right" offset={[6, -8]} className="kk-label">
                      {nachname}
                    </Tooltip>
                  )}
                  <Popup>
                    <div style={{ minWidth: '180px' }}>
                      <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
                        <a
                          href={`?kunde=${k.id}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#9D174D', textDecoration: 'underline' }}
                          title="Akte in neuem Tab oeffnen"
                        >
                          {vollname}
                        </a>
                      </div>
                      <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
                        {k.street && <>{k.street}<br/></>}
                        {k.zip} {k.city}
                      </div>
                      {onAddKunde && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => onAddKunde(k.id, 'planung')}
                            style={{ flex: 1, padding: '5px 8px', background: '#fffbeb', color: '#B45309', border: '1px solid #fde68a', borderRadius: '5px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            title="Als Planung zu dieser Tour hinzufuegen"
                          >
                            + Planung
                          </button>
                          <button
                            onClick={() => onAddKunde(k.id, 'bestaetigt')}
                            style={{ flex: 1, padding: '5px 8px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: '5px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            title="Als bestaetigt zu dieser Tour hinzufuegen"
                          >
                            + Bestätigt
                          </button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>

          {punkte.map(p => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={nummerIcon(p.nummer, p.status)}
              zIndexOffset={1000}
            >
              <Popup>
                <div style={{ minWidth: '180px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
                    {p.nummer}.{' '}
                    {p.kunde_id ? (
                      <a
                        href={`?kunde=${p.kunde_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#9D174D', textDecoration: 'underline' }}
                        title="Akte in neuem Tab oeffnen"
                      >
                        {p.kunde_name || 'Unbenannt'}
                      </a>
                    ) : (
                      p.kunde_name || 'Unbenannt'
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                    {p.kunde_strasse && <>{p.kunde_strasse}<br/></>}
                    {p.kunde_plz} {p.kunde_ort}
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    Status: <strong style={{ color: p.status === 'bestaetigt' ? '#059669' : '#D97706' }}>
                      {p.status === 'bestaetigt' ? `Bestaetigt${p.uhrzeit ? ` · ${p.uhrzeit}` : ''}` : 'In Planung'}
                    </strong>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Start-/Ziel-Anker (eckige dunkle Pins) */}
          {aStart && (
            <Marker position={[aStart.lat, aStart.lng]} icon={ankerIcon(gleicherAnker ? 'S/Z' : 'S')} zIndexOffset={1100}>
              <Tooltip permanent direction="top" offset={[0, -16]} className="kk-label">
                {gleicherAnker ? 'Start & Ziel' : 'Start'}
              </Tooltip>
              <Popup><div style={{ minWidth: '160px', fontSize: '13px' }}>
                <strong>{gleicherAnker ? 'Start & Ziel' : 'Start'}</strong><br />{aStart.label || ''}
              </div></Popup>
            </Marker>
          )}
          {aZiel && !gleicherAnker && (
            <Marker position={[aZiel.lat, aZiel.lng]} icon={ankerIcon('Z')} zIndexOffset={1100}>
              <Tooltip permanent direction="top" offset={[0, -16]} className="kk-label">Ziel</Tooltip>
              <Popup><div style={{ minWidth: '160px', fontSize: '13px' }}>
                <strong>Ziel</strong><br />{aZiel.label || ''}
              </div></Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
